import { useEffect, useState } from 'react';

import ENV from '../constants/Env';
import { captureException, captureMessage } from '../lib/error-handler';

const { apiUrl } = ENV;

export const RIDER_APP_NEW_LOG_IN = 'rider_app_new_log_in';

// Short: the only thing waiting on this is a link on the sign in page.
const FETCH_TIMEOUT_MS = 5000;

// Resolves to null when the flags couldn't be loaded.
async function fetchPublicFeatureFlags(): Promise<string[] | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${apiUrl}/public/custodian_graphql`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: '{ featureFlags }' }),
      signal: controller.signal,
    });

    if (!response.ok) {
      captureMessage(
        `Public feature flag fetch failed: HTTP ${response.status}`,
      );
      return null;
    }

    const json = await response.json();
    // A partial response can't be trusted to hold every enabled flag.
    if (json.errors?.length) {
      captureMessage('Public feature flag fetch returned errors', {
        errors: json.errors,
      });
      return null;
    }

    const flags = json.data?.featureFlags;
    if (!Array.isArray(flags)) {
      captureMessage('Public feature flag fetch returned no featureFlags');
      return null;
    }

    return flags;
  } catch (error) {
    if (controller.signal.aborted) {
      captureMessage(
        `Public feature flag fetch timed out after ${FETCH_TIMEOUT_MS}ms`,
      );
    } else {
      captureException(error);
    }
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// Module-level so flags are shared across the visit, not loaded per mount.
const flagCache = {
  // The last flags loaded successfully. A failed load never clears it.
  lastKnown: null as string[] | null,
  // The request in flight, shared by every caller until it settles.
  inFlight: null as Promise<string[] | null> | null,

  // Ask the API, joining a request already in flight. Resolves to null on
  // failure, leaving lastKnown as it was.
  revalidate(): Promise<string[] | null> {
    this.inFlight ??= fetchPublicFeatureFlags().then(flags => {
      if (flags) this.lastKnown = flags;
      this.inFlight = null;
      return flags;
    });
    return this.inFlight;
  },
};

// For pages shown before sign in, where there is no session to read flags
// from. Only globally enabled flags come back: per-user and per-account gates
// have no user to evaluate yet.
//
// Returns null while nothing is known yet. Flags loaded earlier in the visit
// are returned straight away; without `revalidate` they're used as is, and
// the API is only asked when nothing is known.
//
// `revalidate` also asks the API on every mount and updates to the answer.
// Use it where a stale flag matters, like gating a page: the visitor may have
// loaded the sign in page before the flag was turned on.
//
// When a request fails, the last known flags stay in use; with none, the
// result is [] (the flag-off behavior) and the next mount tries again.
export function usePublicFeatureFlags({
  revalidate = false,
}: { revalidate?: boolean } = {}): string[] | null {
  const [flags, setFlags] = useState<string[] | null>(flagCache.lastKnown);

  useEffect(() => {
    if (!revalidate && flagCache.lastKnown) return;

    let cancelled = false;

    flagCache.revalidate().then(loaded => {
      if (!cancelled) setFlags(loaded ?? flagCache.lastKnown ?? []);
    });

    return () => {
      cancelled = true;
    };
  }, [revalidate]);

  return flags;
}
